import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { db, auth, collection, query, onSnapshot, addDoc, serverTimestamp, orderBy, getDocs, deleteDoc, doc, writeBatch, limit } from '../firebase';
import { chatWithGemini } from '../lib/gemini';
import { motion } from 'framer-motion';
import { 
  Send, 
  Plus, 
  Sparkles,
  Loader2,
  ArrowLeft,
  Copy,
  Check
} from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { toast, Toaster } from 'sonner';
import { trackEvent } from '../lib/analytics';
import { trackUserAction } from '../lib/events';
import { useTasks } from '../contexts/TaskContext';

const ChatInput = ({ onSendMessage, isTyping }: { onSendMessage: (msg: string) => void, isTyping: boolean }) => {
  const [input, setInput] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isTyping) return;
    onSendMessage(input);
    setInput('');
  };

  return (
    <form onSubmit={handleSubmit} className="relative flex gap-2 w-full">
      <div className="relative flex-1">
        <input 
          type="text" 
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Enter your product idea to accelerate..."
          className="tech-input pl-6 pr-16 py-4"
        />
        <button 
          type="submit"
          disabled={!input.trim() || isTyping}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white text-black rounded flex items-center justify-center hover:bg-[#E0E0E0] disabled:opacity-50 transition-all"
        >
          <Send size={16} />
        </button>
      </div>
    </form>
  );
};

export default function Inference({ profile, lastBuild, setLastBuild }: { profile: any, lastBuild?: any, setLastBuild?: any }) {
  const { tasks, startTask, updateTask, completeTask, failTask } = useTasks();
  const synthesisTask = tasks.synthesis; // Synthesis is the umbrella for 'Accelerator' and 'The Assembler'

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get('project');
  
  const [messages, setMessages] = useState<any[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const isAccelerating = synthesisTask.status === 'running' && synthesisTask.metadata?.source === 'inference';
  const [generationTime, setGenerationTime] = useState(0);
  const [cooldown, setCooldown] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let timer: any;
    if (cooldown && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setCooldown(false);
    }
    return () => clearInterval(timer);
  }, [cooldown, timeLeft]);

  useEffect(() => {
    let timer: any;
    if (isAccelerating) {
      timer = setInterval(() => {
        setGenerationTime(prev => prev + 1);
      }, 1000);
    } else {
      setGenerationTime(0);
    }
    return () => clearInterval(timer);
  }, [isAccelerating]);

  const handleGenerateAppTemplate = async (idea: string) => {
    if (!projectId || !auth.currentUser) return;
    if (!profile?.isPro && cooldown) {
      toast.error(`Free Tier Cooldown: ${timeLeft}s remaining`);
      return;
    }

    startTask('synthesis', { projectId, idea, source: 'inference' });
    setIsTyping(true);

    if (!profile?.isPro) {
      setCooldown(true);
      setTimeLeft(60); // 60s cooldown for free tier
    }

    const progressInterval = setInterval(() => {
        updateTask('synthesis', { 
            progress: Math.min(95, tasks.synthesis.progress + 5) 
        });
    }, 1000);

    const prompt = `You are a world-class GTM (Go-To-Market) strategist and technical architect. 
    Generate a complete "App Template Specification" for this product idea: "${idea}".
    
    The specification MUST include:
    1. **App Metadata**: A high-converting name, H1, and sub-headline.
    2. **Strategic Brief**: A concise description of the problem solved and the target audience.
    3. **Section Breakdown**: Define exactly which sections the landing page should have (Hero, Features, Pricing, etc.) and what content goes in each.
    4. **Functional Requirements**: A list of interactive components needed (e.g., forms, toggles, charts).
    5. **Synthesis Prompt**: A final, condensed paragraph that can be used directly as an input for a UI builder to generate the full HTML/CSS/JS.

    FORMATTING: Use clear Markdown headers. Be precise and technical.
    `;

    try {
      await addDoc(collection(db, `projects/${projectId}/chats`), {
        role: 'user',
        content: `🚀 Accelerate App: ${idea}`,
        timestamp: serverTimestamp(),
        projectId
      });

      const response = await chatWithGemini([
        { role: 'user', content: prompt }
      ], "SYSTEM: You are the Yacht Labs App Template Engine. Output high-value, production-ready app specifications in Markdown.");

      // Clean response
      let cleanedResponse = response.trim();
      if (cleanedResponse.startsWith('```')) {
        const lines = cleanedResponse.split('\n');
        if (lines[0].startsWith('```')) lines.shift();
        if (lines[lines.length - 1].startsWith('```')) lines.pop();
        cleanedResponse = lines.join('\n').trim();
      }

      await addDoc(collection(db, `projects/${projectId}/chats`), {
        role: 'model',
        content: response,
        timestamp: serverTimestamp(),
        projectId
      });

      // Save to Asset Vault (Synthesis)
      const docRef = await addDoc(collection(db, `projects/${projectId}/documents`), {
        title: `App Template: ${idea.slice(0, 30)}${idea.length > 30 ? '...' : ''}`,
        content: cleanedResponse,
        type: 'app_template',
        projectId,
        ownerId: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      trackUserAction(auth.currentUser.uid, 'app_template_generated', `Generated app template for: ${idea}`, 'Inference', projectId);

      clearInterval(progressInterval);
      completeTask('synthesis', { cleanedResponse, docId: docRef.id });

      toast.success("App Template Generated", {
        description: "Redirecting to Build Lab to synthesize the blueprint..."
      });
      
      trackEvent('app_template_generated', { projectId });
      
      // Automatic navigation to Build with the generated spec
      setTimeout(() => {
        navigate(`/studio/build?project=${projectId}&spec=${encodeURIComponent(cleanedResponse)}`);
      }, 1500);
    } catch (error: any) {
      clearInterval(progressInterval);
      console.error('Acceleration failed:', error);
      failTask('synthesis', error.message || 'Acceleration failed');
      
      if (error.message?.includes('403') || error.message?.includes('PERMISSION_DENIED') || error.message?.includes('Forbidden')) {
        toast.error("API Key Required", {
          description: "Your project requires a custom Gemini API key to proceed. Please select one.",
          action: {
            label: "Select Key",
            onClick: async () => {
              if ((window as any).aistudio) {
                await (window as any).aistudio.openSelectKey();
              }
            }
          }
        });
      } else {
        toast.error("Acceleration Failed", {
          description: error.message || "An unexpected error occurred."
        });
      }
    } finally {
      setIsTyping(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;

    const draft = searchParams.get('draft');

    // Fetch Messages
    const qMessages = query(
      collection(db, `projects/${projectId}/chats`),
      orderBy('timestamp', 'desc'),
      limit(100)
    );
    const unsubMessages = onSnapshot(qMessages, (snap) => {
      const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() })).reverse();
      setMessages(msgs);
      
      // Only trigger if no messages exist and draft is present
      if (draft && msgs.length === 0 && !isAccelerating) {
        handleGenerateAppTemplate(draft);
      }
    });

    return () => {
      unsubMessages();
    };
  }, [projectId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleClearChat = async () => {
    if (!projectId || !auth.currentUser) return;
    if (!window.confirm("Are you sure you want to clear the chat history? This cannot be undone.")) return;

    const toastId = toast.loading("Clearing chat history...");
    try {
      const q = query(collection(db, `projects/${projectId}/chats`));
      const snapshot = await getDocs(q);
      const batch = writeBatch(db);
      snapshot.docs.forEach((d) => {
        batch.delete(d.ref);
      });
      await batch.commit();
      toast.success("Chat history cleared", { id: toastId });
    } catch (error) {
      console.error("Clear chat failed:", error);
      toast.error("Failed to clear chat", { id: toastId });
    }
  };

  if (!projectId) {
    return (
      <div className="h-full flex items-center justify-center text-zinc-500 font-mono uppercase text-xs tracking-widest">
        Select a project from the dashboard to begin acceleration.
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#050505]">
      <Toaster position="top-right" theme="dark" />
      {/* Module Header */}
      <header className="tech-header">
        <div className="flex items-center gap-6">
          <Link 
            to="/studio"
            className="w-10 h-10 bg-[#111111] border border-[#222222] rounded-lg flex items-center justify-center text-[#888888] hover:text-white hover:bg-[#1A1A1A] transition-all"
          >
            <ArrowLeft size={18} />
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-[#001F3F] border border-[#0074D9]/30 rounded-lg flex items-center justify-center">
              <Sparkles className="text-[#0074D9]" size={18} />
            </div>
            <div>
              <h1 className="tech-label text-white">Accelerator Lab</h1>
              <p className="text-[10px] text-[#888888] font-sans">Strategic Planning & GTM Intelligence</p>
            </div>
          </div>
        </div>

        {messages.length > 0 && (
          <button 
            onClick={handleClearChat}
            className="tech-button-secondary text-[#FF4444] hover:text-[#FF4444] hover:border-[#FF4444]/50"
          >
            <Plus size={14} className="rotate-45" />
            Clear History
          </button>
        )}
      </header>

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden relative">
        <div className="h-full flex flex-col">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 md:p-12 space-y-12 scroll-smooth no-scrollbar">
            {messages.length === 0 && !isAccelerating && (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-8 max-w-md mx-auto">
                <div className="w-20 h-20 bg-[#001F3F] border border-[#0074D9]/30 rounded-2xl flex items-center justify-center shadow-[0_0_30px_rgba(0,116,217,0.2)]">
                  <Sparkles className="text-[#0074D9]" size={32} />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-mono uppercase tracking-tighter text-white">Ready to ship?</h2>
                  <p className="text-sm text-[#888888] leading-relaxed font-sans">Enter your product idea below and I'll generate a complete Launch Package including landing copy, social strategy, and a technical roadmap.</p>
                </div>
                <div className="w-full space-y-3">
                  <button 
                    onClick={() => handleGenerateAppTemplate("A high-performance SaaS for builders")}
                    disabled={!profile?.isPro && cooldown}
                    className="tech-button w-full py-4"
                  >
                    {!profile?.isPro && cooldown ? `Cooldown: ${timeLeft}s` : 'Generate App Template'}
                  </button>
                  {!profile?.isPro && (
                    <p className="text-[10px] text-[#555555] font-mono uppercase tracking-widest">
                      Free Tier: 60s cooldown between generations. <Link to="/studio?upgrade=true" className="text-[#0074D9] hover:underline">Upgrade</Link>
                    </p>
                  )}
                  <p className="text-[10px] text-[#555555] font-mono uppercase tracking-widest">Or start a custom conversation below</p>
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[85%] md:max-w-[75%] space-y-2 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                  <div className="flex items-center gap-2 px-1">
                    <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#555555]">
                      {msg.role === 'user' ? 'You' : 'Accelerator Engine'}
                    </span>
                  </div>
                  <div className={`p-6 md:p-8 rounded-xl relative group/msg ${
                    msg.role === 'user' 
                      ? 'bg-white text-black shadow-xl' 
                      : 'bg-[#111111] text-[#CCCCCC] border border-[#222222]'
                  }`}>
                    {msg.role === 'model' && (
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(msg.content);
                          toast.success("Copied to clipboard");
                        }}
                        className="absolute top-4 right-4 p-2 bg-[#050505] hover:bg-[#1A1A1A] rounded opacity-0 group-hover/msg:opacity-100 transition-all text-[#888888] hover:text-white border border-[#333333]"
                        title="Copy output"
                      >
                        <Copy size={14} />
                      </button>
                    )}
                    <div className={`prose prose-sm max-w-none prose-p:leading-relaxed prose-pre:bg-[#050505] prose-pre:border prose-pre:border-[#222222] prose-pre:p-4 prose-pre:rounded-lg overflow-x-auto ${msg.role === 'model' ? 'prose-invert prose-zinc prose-headings:font-mono prose-headings:uppercase prose-headings:tracking-tight prose-headings:text-white prose-a:text-[#0074D9]' : ''}`}>
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
            {(isTyping || isAccelerating) && (
              <div className="flex justify-start">
                <div className="bg-[#111111] p-6 rounded-xl border border-[#222222] flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin text-[#888888] mr-2" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#888888]">Accelerating...</span>
                  </div>
                  <div className="flex items-center gap-2 px-1">
                    <div className="w-1.5 h-1.5 bg-[#0074D9] rounded-full animate-pulse" />
                    <span className="text-[10px] font-mono text-[#555555]">Elapsed: {generationTime}s</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="p-6 md:p-12 pt-0 border-t border-[#222222] bg-[#050505]">
            <div className="max-w-4xl mx-auto w-full mt-6">
              <ChatInput onSendMessage={handleGenerateAppTemplate} isTyping={isTyping || isAccelerating} />
              <p className="mt-4 text-[9px] text-[#555555] text-center font-mono uppercase tracking-widest">
                Accelerator Engine generates high-value GTM assets. Verify critical architecture.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
